import type { ActionProperties, ProcessTemplateVersionSummary } from "@octopusdeploy/octopus-server-client";
import type { ProcessTemplateConsumerActionDispatcher } from "~/analytics/Analytics";
import { ProcessTemplateConsumerAction } from "~/analytics/Analytics";
import { getProcessTemplateVersionMaskFromActionProperties } from "~/areas/projects/components/Process/ProcessTemplates/processTemplateId";
import { getUpdatedVersionMask, mapMaskToUpdateBehaviour, TemplateVersioningSection, UpdateBehaviour, UsageConstraint } from "~/areas/projects/components/Templates/TemplateVersioningSection";
import { useIsLicensedFeatureEnabled } from "~/components/LicensedFeature/useIsLicensedFeatureEnabled";
interface ProcessTemplateVersioningSectionProps {
    initialActionProperties: ActionProperties;
    initialProcessTemplateVersion: string | undefined | null;
    setVersionMaskProperty: (versionMask: string) => void;
    latestProcessTemplateVersionSummary: ProcessTemplateVersionSummary | null;
    selectedUsageConstraint: UsageConstraint;
    setSelectedUsageConstraint: (value: UsageConstraint) => void;
    selectedUpdateBehaviour: UpdateBehaviour;
    setSelectedUpdateBehaviour: (value: UpdateBehaviour) => void;
    processTemplateConsumerActionDispatch: ProcessTemplateConsumerActionDispatcher;
    processTemplateSlug: string;
    onUpdateToLatestVersion: () => void;
    currentVersion: string | undefined;
    templateName: string;
}
export function ProcessTemplateVersioningSection({ initialActionProperties, initialProcessTemplateVersion, setVersionMaskProperty, latestProcessTemplateVersionSummary, selectedUsageConstraint, setSelectedUsageConstraint, selectedUpdateBehaviour, setSelectedUpdateBehaviour, processTemplateConsumerActionDispatch, processTemplateSlug, onUpdateToLatestVersion, currentVersion, templateName, }: ProcessTemplateVersioningSectionProps) {
    const initialVersionMask = getProcessTemplateVersionMaskFromActionProperties(initialActionProperties);
    const initialUpdateBehavior = mapMaskToUpdateBehaviour(initialVersionMask);
    const isFeatureEnabled = useIsLicensedFeatureEnabled("ProcessTemplates");
    const onUpdateBehaviourChanged = (value: UpdateBehaviour) => {
        if (!latestProcessTemplateVersionSummary?.LatestStableVersion) {
            throw Error("Cannot change update behaviour when no stable version is available.");
        }
        setSelectedUpdateBehaviour(value);
        const versionMask = getUpdatedVersionMask(latestProcessTemplateVersionSummary.LatestStableVersion, initialProcessTemplateVersion, value, initialUpdateBehavior);
        setVersionMaskProperty(versionMask);
        processTemplateConsumerActionDispatch("Process Template Version Preferences Set", {
            type: "Process Template Version Preferences Set",
            action: ProcessTemplateConsumerAction.SelectVersionPreference,
            processTemplateId: processTemplateSlug,
            versioningPreference: value,
            isPreRelease: false,
        });
    };
    const onUsageConstraintChanged = (value: UsageConstraint) => {
        if (value === UsageConstraint.PreRelease) {
            setVersionMaskProperty(UpdateBehaviour.PreRelease);
            setSelectedUsageConstraint(UsageConstraint.PreRelease);
            processTemplateConsumerActionDispatch("Process Template Version Preferences Set", {
                type: "Process Template Version Preferences Set",
                action: ProcessTemplateConsumerAction.SelectVersionPreference,
                processTemplateId: processTemplateSlug,
                versioningPreference: UpdateBehaviour.PreRelease,
                isPreRelease: true,
            });
        }
        else {
            setSelectedUsageConstraint(value);
            onUpdateBehaviourChanged(UpdateBehaviour.Minor);
        }
    };
    return (<TemplateVersioningSection templateName={templateName} currentVersion={currentVersion} latestStableVersion={latestProcessTemplateVersionSummary?.LatestStableVersion} latestPreReleaseVersion={latestProcessTemplateVersionSummary?.LatestPreReleaseVersion} isOnlyPreRelease={latestProcessTemplateVersionSummary?.IsOnlyPreRelease ?? false} selectedUsageConstraint={selectedUsageConstraint} selectedUpdateBehaviour={selectedUpdateBehaviour} onUsageConstraintChanged={onUsageConstraintChanged} onUpdateBehaviourChanged={onUpdateBehaviourChanged} onUpdateToLatestVersion={onUpdateToLatestVersion} isFeatureEnabled={isFeatureEnabled}/>);
}
